import type { DateTimeFilterBase } from '../types';

import {
  getDateTimeFilterByAlias,
  getDateTimeFilterMapping,
  MainDateTimePickerAlias,
} from '../constants/mainDataTimePicker';
import { isSameDayTimestamp, formatTimeRange } from './parse-date';
import { dayjsTz, convertDateToTz } from '~/utils/dayjs';
import { DATE_FORMAT } from '../constants/datePicker';

interface IDateRangeLabelParams {
  since: number | string;
  until: number | string;
  isShowNow?: boolean;
}

export function getDateRangeTitle(since: number | string, until: number | string): string {
  const sinceDate = convertDateToTz(since);
  const untilDate = convertDateToTz(until);

  if (sinceDate.isSame(untilDate, 'day')) {
    return sinceDate.format(DATE_FORMAT.SHORT);
  }

  if (sinceDate.isSame(untilDate, 'month') && sinceDate.isSame(untilDate, 'year')) {
    return `${sinceDate.format(DATE_FORMAT.MONTH_DAY)} - ${untilDate.format(DATE_FORMAT.DAY_YEAR)}`;
  }

  if (sinceDate.isSame(untilDate, 'year')) {
    return `${sinceDate.format(DATE_FORMAT.MONTH_DAY)} - ${untilDate.format(DATE_FORMAT.SHORT_NO_PAD)}`;
  }

  return `${sinceDate.format(DATE_FORMAT.SHORT_NO_PAD)} - ${untilDate.format(DATE_FORMAT.SHORT_NO_PAD)}`;
}

/**
 * Resolves a { title, alias } pair from a since/until date range.
 * Matches against known presets (today, last 7 days, …); falls back to
 * a formatted custom range with alias "custom".
 */
export function getDateTimeFilterBase(
  since: number | string,
  until: number | string,
): DateTimeFilterBase {
  const now = dayjsTz();
  const sinceDate = convertDateToTz(since);
  const untilDate = convertDateToTz(until);

  const mappings = getDateTimeFilterMapping();
  for (const mapping of Object.values(mappings)) {
    const range = mapping.getDateRange(now);
    const expectedSince = convertDateToTz(range.since);
    const expectedUntil = convertDateToTz(range.until);

    const isSameSince = sinceDate.isSame(expectedSince, 'day');
    const isSameUntil = untilDate.isSame(expectedUntil, 'day');

    if (isSameSince && isSameUntil) {
      return { title: mapping.title, alias: mapping.alias };
    }
  }

  return {
    title: getDateRangeTitle(since, until),
    alias: 'custom',
  };
}

export function getVersionDateRangeTitle(data: IDateRangeLabelParams): string {
  const { since, until, isShowNow } = data;

  const sinceDate = convertDateToTz(since);
  const isToday = sinceDate.isSame(dayjsTz(), 'day');

  if (isToday) return getDateTimeFilterByAlias(MainDateTimePickerAlias.TODAY).title;
  if (isShowNow) return `${sinceDate.format(DATE_FORMAT.SHORT_NO_PAD)} - Now`;

  return getDateRangeTitle(since, until);
}

export const getVersionDateDescription = ({ since, until, isShowNow }: IDateRangeLabelParams) => {
  const isSameDay = isSameDayTimestamp(since, until);
  if (!isSameDay) return undefined;

  return formatTimeRange({ since, until, showNow: isShowNow });
};
